import { ChallengeService } from '@duck/api-definitions'
import type { TranslateFunction, TranslationKey } from '@/modules'

/**
 * Shared display helpers for assignments — used by the assignment card, the
 * detail drawer, and the start hook so the action label, gating, and time
 * formatting stay identical everywhere an assignment is shown.
 */

const actionButtonMap: Record<ChallengeService.AvailableChallengeStatusEnum, { text: TranslationKey, className: string }> = {
  [ChallengeService.AvailableChallengeStatusEnum.Available]: { text: 'challenges.startChallenge', className: 'bg-accent hover:bg-accent/80' },
  [ChallengeService.AvailableChallengeStatusEnum.Unbooted]: { text: 'challenges.continue', className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow' },
  [ChallengeService.AvailableChallengeStatusEnum.Ongoing]: { text: 'challenges.continue', className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow' },
  [ChallengeService.AvailableChallengeStatusEnum.Cooldown]: { text: 'challenges.retakeInTIME', className: 'bg-accent/20 hover:bg-accent/30' },
  [ChallengeService.AvailableChallengeStatusEnum.Finished]: { text: 'challenges.retake', className: 'bg-success hover:bg-success/80' },
  [ChallengeService.AvailableChallengeStatusEnum.Aborted]: { text: 'challenges.resume', className: 'bg-warning hover:bg-warning/80' },
}

export const getTimeDifferenceText = (isoDateString: string, t: TranslateFunction): string => {
  const diffInMs = new Date(isoDateString).getTime() - Date.now()
  if (diffInMs <= 0) return ''
  const diffInMinutes = Math.floor(diffInMs / 1000 / 60)
  const diffInHours = Math.floor(diffInMinutes / 60)
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays > 0) return diffInDays === 1 ? t('times.oneDay') : t('times.xDays', { x: diffInDays })
  if (diffInHours > 0) return diffInHours === 1 ? t('times.oneHour') : t('times.xHours', { x: diffInHours })
  if (diffInMinutes > 0) return diffInMinutes === 1 ? t('times.oneMinute') : t('times.xMinutes', { x: diffInMinutes })
  return t('times.verySoon')
}

export const getTimeLimitDisplay = (timeLimitMinutes: number, t: TranslateFunction): string => {
  if (timeLimitMinutes < 60) return timeLimitMinutes === 1 ? t('times.oneMinute') : t('times.xMinutes', { x: timeLimitMinutes })
  if (timeLimitMinutes % 60 === 0) {
    const hours = timeLimitMinutes / 60
    return hours === 1 ? t('times.oneHour') : t('times.xHours', { x: hours })
  }
  return t('times.xMinutes', { x: timeLimitMinutes })
}

export interface AssignmentActionButton {
  text: TranslationKey | string
  className: string
  disabled: boolean
  isCustomText: boolean
}

export const getAssignmentActionButton = (
  status: ChallengeService.AvailableChallengeStatusEnum,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  t: TranslateFunction
): AssignmentActionButton => {
  const now = new Date()
  if (endDate && new Date(endDate) < now) {
    return { text: 'challenges.submissionClosed', className: 'bg-accent/20 hover:bg-accent/30', disabled: true, isCustomText: false }
  }
  if (startDate && new Date(startDate) > now) {
    return { text: t('challenges.startInX', { x: getTimeDifferenceText(startDate, t) }), className: 'bg-accent/20 hover:bg-accent/30', disabled: true, isCustomText: true }
  }
  const config = actionButtonMap[status] || { text: 'challenges.startChallenge', className: 'bg-accent hover:bg-accent/80' }
  return { ...config, disabled: status === ChallengeService.AvailableChallengeStatusEnum.Cooldown, isCustomText: false }
}

/** Locale-aware medium date + short time, e.g. "Jun 27, 2026, 3:00 PM". */
export const formatAssignmentDate = (isoDateString: string, locale: string): string =>
  new Date(isoDateString).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
